import { Suspense, useEffect, useState } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Spin } from 'antd'
import { MainLayout } from './layouts/MainLayout'
import { routes } from './config/routes'
import { useAppStore } from './store'
import { DevRouteLauncher } from './components/feature/dev-tools/DevRouteLauncher'
import { setupDouyinImTaskQueueBridge } from './store/douyinImTaskQueue'
import { IPC } from '../common/ipc/channels'
import { DEFAULT_APP_NAME, resolveAppName } from '../common/appBrand'
import { isElectron } from '../common/utils/platform'
import { appReporter } from './utils/appReporter'
import { useAppUpdateListener } from './hooks/useAppUpdateListener'
import { ForceUpdateOverlay } from './components/feature/app-update/ForceUpdateOverlay'
import { fetchCurrentUser, getProjectConfig } from './api'

const ENABLE_DEV_ROUTE_LAUNCHER = (
  import.meta.env.VITE_ENABLE_DEV_ROUTE_LAUNCHER
  ?? (import.meta.env.DEV ? 'true' : 'false')
) === 'true'

function RendererReadyReporter() {
  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      window.electronAPI?.send(IPC.RENDERER_READY)
    })

    return () => {
      window.cancelAnimationFrame(frameId)
    }
  }, [])

  return null
}

function App() {
  const location = useLocation()
  const isAuthenticated = useAppStore((state) => Boolean(state.auth.userId))
  const { contextHolder: updateContextHolder } = useAppUpdateListener()
  const authUserId = useAppStore((state) => state.auth.userId)
  const authUser = useAppStore((state) => state.auth.user)
  const setAuthSession = useAppStore((state) => state.setAuthSession)
  const clearAuthSession = useAppStore((state) => state.clearAuthSession)
  const appName = useAppStore((state) => state.appConfig.appName)
  const setAppName = useAppStore((state) => state.setAppName)
  const [authChecked, setAuthChecked] = useState(false)
  const loginRoute = routes.find((route) => route.path === '/login')
  const appRoutes = routes.filter((route) => route.path !== '/login')
  const defaultPath = isAuthenticated ? '/marketing-operator' : '/login'

  useEffect(() => {
    document.title = appName
  }, [appName])

  useEffect(() => {
    let cancelled = false;
    const applyAppName = (value?: string | null) => {
      const nextAppName = resolveAppName(value);
      setAppName(nextAppName);
      void window.electronAPI?.invoke(IPC.SET_APP_NAME, { appName: nextAppName });
    };

    getProjectConfig()
      .then((config) => {
        if (!cancelled) {
          applyAppName(config?.appName);
        }
      })
      .catch(() => {
        if (!cancelled) {
          applyAppName(DEFAULT_APP_NAME);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [setAppName])

  useEffect(() => {
    const pagePath = `${location.pathname || '/'}${location.search || ''}`
    appReporter.syncRouteSnapshot(pagePath)
  }, [location.pathname, location.search])

  useEffect(() => {
    let cleanup = () => {};
    let cancelled = false;

    const syncTaskQueueBridge = async () => {
      const currentUserId = authUserId.trim();

      if (!window.electronAPI) {
        if (!currentUserId) {
          useAppStore.getState().replaceDouyinImTaskQueue({});
        }
        return;
      }

      await window.electronAPI.invoke(IPC.SET_ACTIVE_APP_USER, {
        userId: currentUserId,
      });

      if (cancelled) {
        return;
      }

      if (!currentUserId) {
        useAppStore.getState().replaceDouyinImTaskQueue({});
        return;
      }

      // 主进程队列负责承接浏览器执行中的状态，renderer 只做展示和结果同步。
      cleanup = setupDouyinImTaskQueueBridge();
    };

    void syncTaskQueueBridge();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [authUserId, isAuthenticated]);

  useEffect(() => {
    let cancelled = false;

    void fetchCurrentUser()
      .then((payload) => {
        if (cancelled) {
          return;
        }
        setAuthSession({
          userId: payload.user_id,
          user: {
            id: payload.user_id,
            nickname: payload.username,
            phone: payload.phone,
            isAdmin: Boolean(payload.is_admin),
            invitationCode: payload.invitationCode,
            avatar: authUser?.avatar,
          },
        });
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        console.error('Failed to refresh current user profile.', error);
        clearAuthSession();
      })
      .finally(() => {
        if (!cancelled) {
          setAuthChecked(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [authUser?.avatar, clearAuthSession, setAuthSession]);

  if (!authChecked) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Suspense fallback={
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    }>
      {updateContextHolder}
      {isElectron() && <RendererReadyReporter />}
      {isElectron() && <ForceUpdateOverlay />}
      <Routes>
        {loginRoute && (
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/marketing-operator" replace /> : loginRoute.element}
          />
        )}
        <Route path="/" element={<Navigate to={defaultPath} replace />} />
        <Route
          path="/"
          element={isAuthenticated ? <MainLayout /> : <Navigate to="/login" replace />}
        >
          {appRoutes.map((route) => {
            return (
              <Route 
                key={route.path} 
                path={route.path.replace(/^\//, '')} 
                element={route.element} 
              />
            );
          })}
        </Route>
        <Route path="*" element={<Navigate to={defaultPath} replace />} />
      </Routes>
      {ENABLE_DEV_ROUTE_LAUNCHER ? <DevRouteLauncher /> : null}
    </Suspense>
  )
}

export default App
